import { Button } from "@/shared/ui/button.tsx";
import { GlowingEffect } from "@/shared/ui/glow-effect-border.tsx";
import { Calendar1, CalendarIcon, LayoutTemplate, Milestone, Sparkles } from "lucide-react";
import { motion } from "motion/react";
import { CampaignStory, CreatePostFormType } from "../../types";
import { format, isPast, isSameDay, startOfDay } from "date-fns";
import { Input } from "@/shared/ui/input.tsx";
import { UseFormReturn, useWatch } from "react-hook-form";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/shared/ui/popover.tsx"
import { Calendar } from "@/shared/ui/calendar.tsx";
import { cn } from "@/lib/utils";
import { useCreateThemePosts, useGetPostSuggestions } from "../../api";
import RegenerateThemeLoader from "@/shared/loaders/RegenerateThemeLoader";
import PostHistory from "./PostHistory";
import { useAtomValue } from "jotai";
import { themeAtom } from "@/features/app-layout/atoms/theme-atoms";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/shared/ui/form.tsx"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/ui/select.tsx"
import { SetStateAction, useEffect } from "react";
import { useGetCreatedThemeImage } from "../../api/themes";
import { useCreateCampaignStory } from "../../api/campaign";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/shared/ui/accordion.tsx"
import { useNavigate } from "react-router-dom";

type Props = {
  setPosts: (id: number, name: string) => void,
  setCampaignPosts: React.Dispatch<SetStateAction<CampaignStory[]>>,
  generateImage: boolean,
  setGenerateImage: React.Dispatch<SetStateAction<boolean>>,
  createForm: UseFormReturn<CreatePostFormType>
}

const CreateThemeForm = ({ setPosts, createForm: form, setCampaignPosts }: Props) => {

  const generateImage = true

  const { suggestedEvents } = useGetPostSuggestions()

  const { createSocialPost, status, generatedTheme } = useCreateThemePosts(!generateImage ? (data: number, name: string) => setPosts(data, name) : undefined)

  const { generatedCampaignStory, createSocialCampaign, status: campaignStatus } = useCreateCampaignStory(setPosts)

  const navigate = useNavigate()

  const { generateImage: generateThemeImage } = useGetCreatedThemeImage((data: number, name: string) => setPosts(data, name))

  useEffect(() => {
    if (campaignStatus === "success" && generatedCampaignStory) {

      setCampaignPosts(generatedCampaignStory.data.stories)
    }
    if (campaignStatus === "success" && generatedCampaignStory) {
      generateThemeImage(generatedCampaignStory.data.event_id)
    }
  }, [campaignStatus])

  useEffect(() => {
    if (status === "success" && generatedTheme) {
      generateThemeImage(generatedTheme.data.event_id)
    }
  }, [status])


  const dateInput = useWatch({
    control: form.control,
    name: "event_date"
  })

  const activityInput = useWatch({
    control: form.control,
    name: "activity"
  })

  // const { createSocialPost, status }  = useCreateThemePosts()

  const submitHandler = (data: CreatePostFormType) => {
    if (activityInput === "Post") {
      createSocialPost(data)
    }

    if (activityInput === "Campaign") {
      createSocialCampaign(data)
    }

  }

  const appTheme = useAtomValue(themeAtom)

  return (
    <div className="h-full rounded-3xl relative shadow-md shadow-gray-200">
      <GlowingEffect
        borderWidth={3}
        spread={50}
        glow={true}
        disabled={false}
        proximity={128}
        inactiveZone={0.01}
      />
      <motion.div className="w-full h-auto flex flex-col gap-3 p-5 bg-white relative rounded-3xl">
        {/* Header space */}
        <div className="flex items-start md:items-center justify-between w-full gap-2 flex-col-reverse md:flex-row">
          <div className="flex items-start flex-col lg:flex-row justify-between w-full max-lg:gap-2">
            <h2 className="text-xl font-medium font-poppins text-gray-600">Create Post</h2>
            <Button className="rounded-full border border-purple-400 flex items-center gap-2 font-poppins tracking-wide text-sm" variant="outline">
              Templates
              <LayoutTemplate className="text-purple-500" />
            </Button>
          </div>
        </div>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(submitHandler)}>
            {!(status === "pending" || campaignStatus === "pending") &&
              <div className="h-full rounded-xl bg-white p-3 bg-clip-padding backdrop-filter backdrop-blur-xl bg-opacity-60 shadow-md shadow-purple-300">
                <p className="font-poppins tracking-wide text-gray-500 h-[2rem]">Context</p>
                <textarea {...form.register("event_description")} rows={5} className="w-full text-sm resize-none bg-transparent focus:border focus:border-gray-200 focus:outline-none rounded-xl p-2 text-gray-700 font-poppins tracking-wider" />
                <div className="w-full flex flex-wrap items-center gap-2">
                  {/*
                    <div className="flex items-center gap-3 cursor-pointer">
                      <Checkbox checked={generateImage} onCheckedChange={(val: boolean) => setGenerateImage(val)} className="data-[state=checked]:bg-purple-600" />
                      <Label onClick={() => setGenerateImage(prev => !prev)}>Generate images for theme ?</Label>
                    </div>
                  */}
                </div>

              </div>
            }
            {(status === "pending" || campaignStatus === "pending") && (
              <RegenerateThemeLoader />
            )}
            <div className="w-full flex items-center justify-between flex-col lg:flex-row gap-2 pt-2">
              <div className="flex flex-wrap items-center gap-2">

                <Popover modal>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "border border-purple-300 rounded-xl",
                        !dateInput && "text-muted-foreground",
                      )}
                    >
                      {dateInput ? (
                        format(dateInput, "PPP")
                      ) : (
                        <span>Pick a date</span>
                      )}
                      <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={dateInput}
                      onSelect={(date: Date | undefined) => {
                        if (date) {
                          form.setValue("event_date", date, { shouldDirty: true })
                        }
                      }}
                      disabled={date => {
                        const today = startOfDay(new Date());
                        const currentDate = startOfDay(date);
                        return isPast(currentDate) && !isSameDay(currentDate, today);
                      }}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
                <FormField
                  control={form.control}
                  name="event_time"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <Input {...field} className="border border-purple-300 rounded-xl w-32 placeholder:text-sm placeholder:font-poppins font-poppins text-sm" type="time" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="activity"
                  render={({ field }) => (
                    <FormItem>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger className="border border-purple-300 rounded-xl placeholder:text-sm placeholder:font-poppins font-poppins text-sm">
                            <SelectValue placeholder="Select type of theme" className="border border-purple-300 rounded-xl w-32 placeholder:text-sm placeholder:font-poppins font-poppins text-sm" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="Post">Post</SelectItem>
                          <SelectItem value="Campaign">Campaign</SelectItem>
                        </SelectContent>
                      </Select>
                    </FormItem>
                  )}
                />

                {(activityInput === "Campaign") && (
                  <FormField
                    control={form.control}
                    name="campiagn_timeline"
                    render={({ field }) => (
                      <FormItem className="flex flex-col">
                        <Popover>
                          <PopoverTrigger asChild>
                            <FormControl>
                              <Button
                                variant={"outline"}
                                className={cn(
                                  "border border-purple-300 rounded-xl",
                                  !field.value && "text-muted-foreground"
                                )}
                              >
                                {field.value ? (
                                  format(field.value, "PPP")
                                ) : (
                                  <span>Pick Campaign end date</span>
                                )}
                                <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                              </Button>
                            </FormControl>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0" align="start">
                            <Calendar
                              mode="single"
                              selected={field.value}
                              onSelect={field.onChange}
                              disabled={(date) =>
                                date < new Date() || date < new Date("1900-01-01")
                              }
                              captionLayout="dropdown"
                            />
                          </PopoverContent>
                        </Popover>
                      </FormItem>
                    )}
                  />
                )
                }
              </div>
              <div className="flex items-center gap-2">
                <Button
                  disabled={!appTheme.isClient}
                  type="submit"
                  variant="outline"
                  className="w-full rounded-full font-poppins border-2 border-purple-400 hover:bg-white tracking-wide text-purple-600">
                  <Sparkles />
                  Generate
                </Button>

              </div>

            </div>

          </form>
        </Form>
        <Accordion
          type="single"
          collapsible
          className="w-full"
        >
          <AccordionItem value="item-1">
            <AccordionTrigger>
              <span className="flex items-center gap-3 ps-2">
                <Milestone size={14} /> Previously created theme
              </span>
            </AccordionTrigger>
            <AccordionContent>
              <div className="w-full grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-2 py-4">
                {suggestedEvents && suggestedEvents.data.slice(-9).map(( item, index ) => (
                  <PostHistory key={`${item.event_id}_${index}`} date={item.start_date} name={item.event_name} onClick={() => {
                    form.setValue("event_date", new Date(item.start_date))
                    form.setValue("event_description", item.event_description)
                    form.setValue("event_time", item.event_time)
                    form.handleSubmit(submitHandler)()
                  }} />
                ))}
              </div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
        <span onClick={() => navigate('/home?curr=campaign')} className="flex items-center gap-3 ps-2 w-full py-3 text-sm font-medium hover:underline cursor-pointer">
          <Calendar1 size={14} /> Previously created campaign
        </span>
      </motion.div>
    </div>
  )
}

export default CreateThemeForm;
